if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.recipientFields = this.querySelector('.recipient-fields');
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.currentProductVariantId = this.dataset.productVariantId;

        if (this.offsetProperty) {
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        }

        if (this.checkboxInput) {
          this.checkboxInput.disabled = false;
        }

        if (this.hiddenControlField) {
          this.hiddenControlField.disabled = true;
        }

        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      connectedCallback() {
        this.cartErrorHandler = this.onCartError.bind(this);
        this.cartUpdateHandler = this.onCartUpdate.bind(this);
        document.addEventListener('cart:add:error', this.cartErrorHandler);
        document.addEventListener('cart:updated', this.cartUpdateHandler);
      }

      disconnectedCallback() {
        document.removeEventListener('cart:add:error', this.cartErrorHandler);
        document.removeEventListener('cart:updated', this.cartUpdateHandler);
      }

      onCartError(event) {
        const { variantId, errors, message } = event.detail || {};
        if (!variantId || variantId.toString() !== this.currentProductVariantId) return;
        this.displayErrorMessage(message, errors);
      }

      onCartUpdate() {
        if (this.checkboxInput?.checked) {
          this.resetRecipientForm();
        }
      }

      onChange() {
        if (!this.checkboxInput) return;

        if (this.checkboxInput.checked) {
          this.recipientFields?.classList.remove('hidden');
          this.enableInputFields();
          if (this.recipientFieldsLiveRegion) {
            this.recipientFieldsLiveRegion.innerText = 'Gift card recipient form expanded';
          }
        } else {
          this.recipientFields?.classList.add('hidden');
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          if (this.recipientFieldsLiveRegion) {
            this.recipientFieldsLiveRegion.innerText = 'Gift card recipient form collapsed';
          }
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput].filter(Boolean);
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty].filter(Boolean);
      }

      clearInputFields() {
        this.inputFields().forEach((field) => {
          field.value = '';
        });
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => {
          field.disabled = false;
        });
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => {
          field.disabled = true;
        });
      }

      formatErrorValue(value) {
        if (Array.isArray(value)) {
          return value.map((entry) => this.formatErrorValue(entry)).join(', ');
        }

        if (typeof value === 'string') {
          return value;
        }

        if (value && typeof value === 'object') {
          return Object.values(value)
            .map((entry) => this.formatErrorValue(entry))
            .join(', ');
        }

        return '';
      }

      displayErrorMessage(title, body) {
        if (!this.errorMessageWrapper) return;

        let errorTitle = title;
        let errorBody = body;

        if (typeof errorBody !== 'object' || errorBody === null) {
          if (typeof errorTitle === 'object' && errorTitle !== null) {
            errorBody = errorTitle;
            errorTitle = 'Unable to add gift card to cart.';
          }
        }

        if (typeof errorTitle !== 'string') {
          errorTitle = 'Unable to add gift card to cart.';
        }

        this.clearErrorMessage();
        this.errorMessageWrapper.classList.remove('hidden');

        if (typeof errorBody === 'object' && errorBody !== null) {
          if (this.errorMessage) {
            this.errorMessage.innerText = errorTitle;
          }

          Object.entries(errorBody).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key === 'send_on' ? 'send-on' : key}-${this.dataset.sectionId}`;
            const message = this.formatErrorValue(value);
            if (!message) return;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');

            if (this.errorMessageList) {
              const li = document.createElement('li');
              const a = document.createElement('a');
              a.setAttribute('href', fieldSelector);
              a.innerText = message;
              li.appendChild(a);
              this.errorMessageList.appendChild(li);
            }

            if (errorTextElement) {
              errorTextElement.innerText = `${message}.`;
              errorMessageElement.classList.remove('hidden');
            }

            const inputKey = key === 'send_on' ? 'sendon' : key;
            const inputElement = this[`${inputKey}Input`];
            if (inputElement) {
              inputElement.setAttribute('aria-invalid', 'true');
              inputElement.setAttribute('aria-describedby', errorMessageId);
            }
          });
          return;
        }

        if (this.errorMessage) {
          this.errorMessage.innerText = this.formatErrorValue(body) || errorTitle;
        }
      }

      clearErrorMessage() {
        if (!this.errorMessageWrapper) return;

        this.errorMessageWrapper.classList.add('hidden');
        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        this.inputFields().forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', 'false');
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput?.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
          this.onChange();
        }
      }
    }
  );
}
